import { ReactNode, createContext, useContext, useRef, useState } from 'react'
import { AppSessionType } from '~/features/appSession/common'
import { FormConfigurationModal } from '../../client/components/Modal'
import { FormConfigurationModalFlows } from '../../client/components/ModalFlows'

type OnSubmitted = (appSession: AppSessionType) => void

const useInternal = () => {
  const refOnSubmitted = useRef<OnSubmitted>(null)
  const [appSessionIdSelected, setAppSessionIdSelected] = useState<string>()
  const [type, setType] = useState<'ONBOARDING' | 'FLOW'>()

  const openOnboardingModal = (id: string, onSubmitted?: () => void) => {
    setAppSessionIdSelected(id)
    setType('ONBOARDING')
    refOnSubmitted.current = onSubmitted
  }

  const openFlowModal = (id: string, onSubmitted?: () => void) => {
    setAppSessionIdSelected(id)
    setType('FLOW')
    refOnSubmitted.current = onSubmitted
  }

  const closeOnboardingModal = (appSession?: AppSessionType) => {
    if (appSession && refOnSubmitted.current) {
      refOnSubmitted.current(appSession)
    }

    setAppSessionIdSelected(null)
    setType(null)
    refOnSubmitted.current = null
  }

  return {
    appSessionIdSelected,
    type,
    openOnboardingModal,
    closeOnboardingModal,
    openFlowModal,
    closeFlowModal: closeOnboardingModal,
  }
}

type FormConfigurationContext = ReturnType<typeof useInternal>

const FormConfigurationContext =
  createContext<FormConfigurationContext>(undefined)

export const FormConfigurationProvider = ({
  children,
}: {
  children: ReactNode
}) => {
  const value = useInternal()

  return (
    <FormConfigurationContext.Provider value={value}>
      {children}

      {value.appSessionIdSelected && value.type === 'ONBOARDING' && (
        <FormConfigurationModal
          appSessionId={value.appSessionIdSelected}
          onCancel={value.closeOnboardingModal}
          onSubmitted={value.closeOnboardingModal}
        />
      )}

      {value.appSessionIdSelected && value.type === 'FLOW' && (
        <FormConfigurationModalFlows
          appSessionId={value.appSessionIdSelected}
          onCancel={value.closeFlowModal}
          onSubmitted={value.closeFlowModal}
        />
      )}
    </FormConfigurationContext.Provider>
  )
}

export const useFormConfigurationContext = (): FormConfigurationContext => {
  const context = useContext(FormConfigurationContext)

  if (context === undefined) {
    throw new Error(
      'useFormConfigurationContext must be used within a FormConfigurationProvider',
    )
  }

  return context
}
